import { API_BASE_URL } from './config';
import { apiFetch } from './apiFetch';

export type QuestionOption = {
  id?: string;
  text: string;
  value: number;
  dimension?: string;
  orderIndex?: number;
};

export type TestQuestion = {
  id?: string;
  text: string;
  /** Matches backend: 'single_choice' | 'multi_choice' | 'scale' | 'text' */
  type: 'single_choice' | 'multi_choice' | 'scale' | 'text';
  dimension?: string;
  order?: number;
  orderIndex?: number;
  options: QuestionOption[];
};

export type PsychometricTestDto = {
  id: string;
  companyId: string;
  /** Matches backend field name */
  name: string;
  description?: string;
  /** Matches backend field name */
  type: string;
  category?: string;
  /** Matches backend field name */
  estimatedTime?: number;
  questions: TestQuestion[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type TestResultDto = {
  id: string;
  testId: string;
  userId: string;
  scores: Record<string, number>;
  rawScores?: Record<string, number>;
  totalScore?: string | number;
  interpretation?: string;
  completedAt: string;
};

export async function listPsychometricTests(companyId: string) {
  const res = await apiFetch(
    `${API_BASE_URL}/api/psychometric-tests/company/${companyId}`,
    { headers: { 'Content-Type': 'application/json' } },
  );
  return res.json() as Promise<{
    success: boolean;
    data?: { tests: PsychometricTestDto[] };
    error?: string;
  }>;
}

export async function getPsychometricTest(testId: string) {
  const res = await apiFetch(
    `${API_BASE_URL}/api/psychometric-tests/${testId}`,
    { headers: { 'Content-Type': 'application/json' } },
  );
  return res.json() as Promise<{
    success: boolean;
    data?: { test: PsychometricTestDto };
    error?: string;
  }>;
}

export async function createPsychometricTest(
  input: {
    companyId: string;
    name: string;
    description?: string;
    type: string;
    estimatedTime?: number;
    questions: TestQuestion[];
  },
) {
  const res = await apiFetch(`${API_BASE_URL}/api/psychometric-tests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return res.json() as Promise<{
    success: boolean;
    data?: { test: PsychometricTestDto };
    error?: string;
  }>;
}

export async function updatePsychometricTest(
  testId: string,
  input: Partial<{
    name: string;
    description: string;
    estimatedTime: number;
    questions: TestQuestion[];
    isActive: boolean;
  }>,
) {
  const res = await apiFetch(`${API_BASE_URL}/api/psychometric-tests/${testId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return res.json() as Promise<{
    success: boolean;
    data?: { test: PsychometricTestDto };
    error?: string;
  }>;
}

export async function submitTestAnswers(
  testId: string,
  input: {
    userId: string;
    companyId: string;
    answers: { questionId: string; selectedOptions?: string[]; textAnswer?: string; value?: number }[];
  },
) {
  // Backend route is /:testId/take (not /submit)
  const res = await apiFetch(`${API_BASE_URL}/api/psychometric-tests/${testId}/take`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return res.json() as Promise<{
    success: boolean;
    data?: { result: TestResultDto };
    error?: string;
  }>;
}

export async function addTestQuestion(
  testId: string,
  input: {
    text: string;
    type: 'single_choice' | 'multi_choice' | 'scale' | 'text';
    weight?: number;
    options?: Array<{ text: string; value: number }>;
  },
) {
  const res = await apiFetch(`${API_BASE_URL}/api/psychometric-tests/${testId}/questions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return res.json() as Promise<{
    success: boolean;
    data?: { question: TestQuestion };
    error?: string;
  }>;
}

export async function deleteTestQuestion(testId: string, questionId: string) {
  const res = await apiFetch(
    `${API_BASE_URL}/api/psychometric-tests/${testId}/questions/${questionId}`,
    { method: 'DELETE', headers: { 'Content-Type': 'application/json' } },
  );
  return res.json() as Promise<{ success: boolean; error?: string }>;
}

export async function reorderTestQuestion(
  testId: string,
  questionId: string,
  newOrderIndex: number,
) {
  const res = await apiFetch(
    `${API_BASE_URL}/api/psychometric-tests/${testId}/questions/${questionId}/order`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newOrderIndex }),
    },
  );
  return res.json() as Promise<{ success: boolean; error?: string }>;
}

export async function toggleTestStatus(testId: string, isActive: boolean) {
  const res = await apiFetch(`${API_BASE_URL}/api/psychometric-tests/${testId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isActive }),
  });
  return res.json() as Promise<{
    success: boolean;
    data?: { test: PsychometricTestDto };
    error?: string;
  }>;
}

export async function getMyTestResults(userId: string, companyId?: string) {
  const params = new URLSearchParams();
  if (companyId) params.set('companyId', companyId);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await apiFetch(
    `${API_BASE_URL}/api/psychometric-tests/results/user/${userId}${qs}`,
    { headers: { 'Content-Type': 'application/json' } },
  );
  return res.json() as Promise<{
    success: boolean;
    data?: { results: TestResultDto[] };
    error?: string;
  }>;
}
